/**
 * Person profile (F1): editable contact info, FAMILY RELATIONS
 * (person↔person), MEMBERSHIPS per church (visitor/believer/member,
 * baptism + date, serves + group, leader, pastor) and account link.
 */
import { msg } from "@lingui/core/macro";
import { createMemo, createSignal, For, Show } from "solid-js";
import {
  getChurchesQuery,
  getGroupsQuery,
  getMembershipsQuery,
  getPeopleQuery,
  getPersonRelationsQuery,
  patchRowVerified,
  queryKeys,
} from "@agape/shared";
import {
  type ChurchGroupRecord,
  type ChurchRecord,
  eq,
  type MembershipRecord,
  membership as membershipTable,
  person_relation,
  type PersonRecord,
  type PersonRelationRecord,
  person as personTable,
} from "@agape/powersync";
import { Heart, Pencil, Plus, Trash2, UserCircle } from "@fleet/icons";

import { useI18n } from "@/providers/i18n-provider";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@fleet/ui/card";
import { PageSubHeader } from "@/components/ui/page-subheader";
import { Select } from "@/components/ui/select";
import { Switch } from "@fleet/ui/switch";
import { showToast } from "@/components/ui/toast";
import { Field, FormDialog } from "./form";
import { newId, nowIso, useDomainQuery, useGrants, useWriter } from "../use-domain";

const RELATION_KINDS = ["spouse", "parent", "child", "sibling", "other"] as const;
const MEMBERSHIP_STATUS = ["visitor", "believer", "member"] as const;

export function PersonDetailPage(props: { personId: string }) {
  const { t } = useI18n();
  const write = useWriter();
  const grants = useGrants();

  const people = useDomainQuery<PersonRecord>(
    (uid) => queryKeys.domain.people(uid),
    (db) => getPeopleQuery(db),
  );
  const relations = useDomainQuery<PersonRelationRecord>(
    (uid) => queryKeys.domain.personRelations(uid),
    (db) => getPersonRelationsQuery(db),
  );
  const memberships = useDomainQuery<MembershipRecord>(
    (uid) => queryKeys.domain.memberships(uid),
    (db) => getMembershipsQuery(db),
  );
  const churches = useDomainQuery<ChurchRecord>(
    (uid) => queryKeys.domain.churches(uid),
    (db) => getChurchesQuery(db),
  );
  const groups = useDomainQuery<ChurchGroupRecord>(
    (uid) => queryKeys.domain.groups(uid),
    (db) => getGroupsQuery(db),
  );

  const current = createMemo(() => (people.data ?? []).find((p) => p.id === props.personId));
  const canWrite = () => grants.can([current()?.church_id], "people", "write");
  const personName = (id: string | null) =>
    (people.data ?? []).find((p) => p.id === id)?.name ?? id ?? "";
  const churchName = (id: string | null) =>
    (churches.data ?? []).find((c) => c.id === id)?.name ?? id ?? "";
  const groupName = (id: string | null) =>
    (groups.data ?? []).find((g) => g.id === id)?.name ?? id ?? "";

  // Family: relations in both directions.
  const family = createMemo(() =>
    (relations.data ?? []).filter(
      (r) => r.person_id === props.personId || r.related_person_id === props.personId,
    ),
  );
  const myMemberships = createMemo(() =>
    (memberships.data ?? []).filter((m) => m.person_id === props.personId),
  );

  // ── Edit contact ─────────────────────────────────────────────────────────
  const [editOpen, setEditOpen] = createSignal(false);
  const [form, setForm] = createSignal<Record<string, string>>({});
  const field = (k: string) => form()[k] ?? "";
  const setField = (k: string, v: string) => setForm((f) => ({ ...f, [k]: v }));
  const openEdit = () => {
    const p = current();
    if (!p) {
      return;
    }
    setForm({
      name: p.name ?? "",
      email: p.email ?? "",
      phone: p.phone ?? "",
      photo: p.photo ?? "",
      address: p.address ?? "",
      birthdate: p.birthdate ?? "",
      notes: p.notes ?? "",
      account_user_id: p.account_user_id ?? "",
    });
    setEditOpen(true);
  };
  const saveEdit = async () => {
    const f = form();
    await patchRowVerified(write(), personTable, personTable.id, props.personId, {
      name: f.name,
      email: f.email || null,
      phone: f.phone || null,
      photo: f.photo || null,
      address: f.address || null,
      birthdate: f.birthdate || null,
      notes: f.notes || null,
      account_user_id: f.account_user_id || null,
    });
    setEditOpen(false);
    showToast(t(msg`Person updated`), "success");
  };

  // ── Family relation ──────────────────────────────────────────────────────
  const [relOpen, setRelOpen] = createSignal(false);
  const [relTarget, setRelTarget] = createSignal("");
  const [relKind, setRelKind] = createSignal<string>("spouse");
  const relTargets = createMemo(() =>
    (people.data ?? [])
      .filter((p) => p.id !== props.personId)
      .map((p) => ({ value: p.id, label: p.name ?? p.id })),
  );
  const addRelation = async () => {
    const p = current();
    if (!relTarget() || !p) {
      return;
    }
    await write().insert(person_relation).values({
      id: newId(),
      church_id: p.church_id,
      person_id: props.personId,
      related_person_id: relTarget(),
      kind: relKind(),
      created_at: nowIso(),
      updated_at: nowIso(),
    });
    setRelOpen(false);
    showToast(t(msg`Family relation added`), "success");
  };
  const removeRelation = async (id: string) => {
    await write().delete(person_relation).where(eq(person_relation.id, id));
  };

  const relationLabel = (kind: string | null) => {
    switch (kind) {
      case "spouse": {
        return t(msg`Spouse`);
      }
      case "parent": {
        return t(msg`Parent`);
      }
      case "child": {
        return t(msg`Child`);
      }
      case "sibling": {
        return t(msg`Sibling`);
      }
      default: {
        return t(msg`Family`);
      }
    }
  };

  // ── Membership ───────────────────────────────────────────────────────────
  const [memOpen, setMemOpen] = createSignal(false);
  const [memId, setMemId] = createSignal<string | null>(null);
  const [memChurch, setMemChurch] = createSignal("");
  const [memStatus, setMemStatus] = createSignal<string>("visitor");
  const [memBaptized, setMemBaptized] = createSignal(false);
  const [memBaptizedAt, setMemBaptizedAt] = createSignal("");
  const [memServes, setMemServes] = createSignal(false);
  const [memServeGroup, setMemServeGroup] = createSignal("");
  const [memLeader, setMemLeader] = createSignal(false);
  const [memPastor, setMemPastor] = createSignal(false);

  const writableChurches = createMemo(() =>
    (churches.data ?? []).filter((c) => grants.can([c.id], "people", "write")),
  );
  const openMembership = (m?: MembershipRecord) => {
    setMemId(m?.id ?? null);
    setMemChurch(m?.church_id ?? current()?.church_id ?? "");
    setMemStatus(m?.status ?? "visitor");
    setMemBaptized(Boolean(m?.baptized));
    setMemBaptizedAt(m?.baptized_at?.slice(0, 10) ?? "");
    setMemServes(Boolean(m?.serves));
    setMemServeGroup(m?.serve_group_id ?? "");
    setMemLeader(Boolean(m?.is_leader));
    setMemPastor(Boolean(m?.is_pastor));
    setMemOpen(true);
  };
  const saveMembership = async () => {
    const values = {
      status: memStatus(),
      baptized: memBaptized() ? 1 : 0,
      baptized_at:
        memBaptized() && memBaptizedAt() ? new Date(memBaptizedAt()).toISOString() : null,
      serves: memServes() ? 1 : 0,
      serve_group_id: memServes() && memServeGroup() ? memServeGroup() : null,
      is_leader: memLeader() ? 1 : 0,
      is_pastor: memPastor() ? 1 : 0,
      updated_at: nowIso(),
    };
    const id = memId();
    if (id) {
      await patchRowVerified(write(), membershipTable, membershipTable.id, id, values);
    } else {
      await write()
        .insert(membershipTable)
        .values({
          id: newId(),
          church_id: memChurch(),
          person_id: props.personId,
          joined_at: nowIso(),
          created_at: nowIso(),
          ...values,
        });
    }
    setMemOpen(false);
    showToast(t(msg`Membership saved`), "success");
  };

  const statusLabel = (status: string | null) => {
    switch (status) {
      case "member": {
        return t(msg`Member`);
      }
      case "believer": {
        return t(msg`Believer`);
      }
      default: {
        return t(msg`Visitor`);
      }
    }
  };

  return (
    <div class="space-y-6">
      <PageSubHeader title={current()?.name ?? t(msg`Person`)} backTo="/people" />
      <Show
        when={current()}
        fallback={
          <p class="text-muted-foreground">{t(msg`This person is not synced to your account.`)}</p>
        }
      >
        {(p) => (
          <>
            <Card data-testid="person-profile">
              <CardContent class="flex items-start justify-between gap-3 py-4">
                <div class="flex min-w-0 items-start gap-3">
                  <span class="flex size-12 shrink-0 items-center justify-center rounded-full bg-accent text-accent-foreground">
                    <Show when={p().photo} fallback={<UserCircle size={24} />}>
                      <img
                        src={p().photo ?? ""}
                        alt=""
                        loading="lazy"
                        decoding="async"
                        class="size-12 rounded-full object-cover"
                      />
                    </Show>
                  </span>
                  <div class="min-w-0 space-y-1">
                    <p class="font-semibold">{p().name}</p>
                    <p class="text-sm text-muted-foreground">
                      {[p().email, p().phone].filter(Boolean).join(" · ")}
                    </p>
                    <p class="text-sm text-muted-foreground">
                      {[p().address, p().birthdate].filter(Boolean).join(" · ")}
                    </p>
                    <Show when={p().notes}>
                      <p class="text-sm text-muted-foreground">{p().notes}</p>
                    </Show>
                    <Show when={p().account_user_id}>
                      <p class="text-xs text-muted-foreground" data-testid="person-account">
                        {t(msg`Linked account`)}: {p().account_user_id}
                      </p>
                    </Show>
                  </div>
                </div>
                <Show when={canWrite()}>
                  <Button
                    size="sm"
                    variant="secondary"
                    data-testid="person-edit"
                    onClick={openEdit}
                  >
                    <Pencil size={14} /> {t(msg`Edit`)}
                  </Button>
                </Show>
              </CardContent>
            </Card>

            {/* ── Familia ───────────────────────────────────────────────── */}
            <section class="space-y-2">
              <div class="flex items-center justify-between">
                <h3 class="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                  {t(msg`Family`)}
                </h3>
                <Show when={canWrite()}>
                  <Button
                    size="sm"
                    variant="ghost"
                    data-testid="family-add"
                    onClick={() => setRelOpen(true)}
                  >
                    <Heart size={14} /> {t(msg`Add relative`)}
                  </Button>
                </Show>
              </div>
              <Show
                when={family().length > 0}
                fallback={
                  <p class="text-sm text-muted-foreground">{t(msg`No family relations yet.`)}</p>
                }
              >
                <For each={family()}>
                  {(r) => {
                    const other = () =>
                      r.person_id === props.personId ? r.related_person_id : r.person_id;
                    return (
                      <Card data-testid="family-item">
                        <CardContent class="flex items-center justify-between gap-3 py-2.5">
                          <p class="min-w-0 truncate text-sm">
                            <span class="font-medium">{personName(other())}</span>{" "}
                            <span class="text-muted-foreground">({relationLabel(r.kind)})</span>
                          </p>
                          <Show when={canWrite()}>
                            <Button
                              size="icon"
                              variant="ghost"
                              aria-label={t(msg`Remove relation`)}
                              onClick={() => void removeRelation(r.id)}
                            >
                              <Trash2 size={14} />
                            </Button>
                          </Show>
                        </CardContent>
                      </Card>
                    );
                  }}
                </For>
              </Show>
            </section>

            {/* ── Membresías ────────────────────────────────────────────── */}
            <section class="space-y-2">
              <div class="flex items-center justify-between">
                <h3 class="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                  {t(msg`Memberships`)}
                </h3>
                <Show when={writableChurches().length > 0}>
                  <Button
                    size="sm"
                    variant="ghost"
                    data-testid="membership-add"
                    onClick={() => openMembership()}
                  >
                    <Plus size={14} /> {t(msg`Add membership`)}
                  </Button>
                </Show>
              </div>
              <Show
                when={myMemberships().length > 0}
                fallback={
                  <p class="text-sm text-muted-foreground">{t(msg`No memberships yet.`)}</p>
                }
              >
                <For each={myMemberships()}>
                  {(m) => (
                    <Card data-testid="membership-item">
                      <CardContent class="flex items-center justify-between gap-3 py-2.5">
                        <div class="min-w-0">
                          <p class="truncate text-sm font-medium">{churchName(m.church_id)}</p>
                          <p class="truncate text-xs text-muted-foreground">
                            {[
                              statusLabel(m.status),
                              m.baptized
                                ? `${t(msg`Baptized`)}${m.baptized_at ? ` (${m.baptized_at.slice(0, 10)})` : ""}`
                                : null,
                              m.serves
                                ? `${t(msg`Serves`)}${m.serve_group_id ? ` · ${groupName(m.serve_group_id)}` : ""}`
                                : null,
                              m.is_leader ? t(msg`Leader`) : null,
                              m.is_pastor ? t(msg`Pastor`) : null,
                            ]
                              .filter(Boolean)
                              .join(" · ")}
                          </p>
                        </div>
                        <Show when={grants.can([m.church_id], "people", "write")}>
                          <Button
                            size="icon"
                            variant="ghost"
                            aria-label={t(msg`Edit membership`)}
                            data-testid="membership-edit"
                            onClick={() => openMembership(m)}
                          >
                            <Pencil size={14} />
                          </Button>
                        </Show>
                      </CardContent>
                    </Card>
                  )}
                </For>
              </Show>
            </section>
          </>
        )}
      </Show>

      {/* ── Diálogos ─────────────────────────────────────────────────────── */}
      <FormDialog
        open={editOpen()}
        onOpenChange={setEditOpen}
        title={t(msg`Edit person`)}
        icon={<UserCircle size={20} />}
        submitTestId="person-edit-submit"
        onSubmit={saveEdit}
      >
        <Field
          id="pe-name"
          label={t(msg`Name`)}
          value={field("name")}
          onInput={(v) => setField("name", v)}
        />
        <div class="grid grid-cols-2 gap-3">
          <Field
            id="pe-email"
            label={t(msg`Email`)}
            value={field("email")}
            onInput={(v) => setField("email", v)}
          />
          <Field
            id="pe-phone"
            label={t(msg`Phone`)}
            value={field("phone")}
            onInput={(v) => setField("phone", v)}
          />
          <Field
            id="pe-birthdate"
            label={t(msg`Birth date`)}
            type="date"
            value={field("birthdate")}
            onInput={(v) => setField("birthdate", v)}
          />
          <Field
            id="pe-photo"
            label={t(msg`Photo URL`)}
            value={field("photo")}
            onInput={(v) => setField("photo", v)}
          />
        </div>
        <Field
          id="pe-address"
          label={t(msg`Address`)}
          value={field("address")}
          onInput={(v) => setField("address", v)}
        />
        <Field
          id="pe-notes"
          label={t(msg`Notes`)}
          value={field("notes")}
          onInput={(v) => setField("notes", v)}
        />
        <Field
          id="pe-account"
          label={t(msg`Linked account (user id)`)}
          value={field("account_user_id")}
          onInput={(v) => setField("account_user_id", v)}
        />
      </FormDialog>

      <FormDialog
        open={relOpen()}
        onOpenChange={setRelOpen}
        title={t(msg`Add relative`)}
        icon={<Heart size={20} />}
        submitTestId="family-add-submit"
        disabled={!relTarget()}
        onSubmit={addRelation}
      >
        <Field id="fr-person" label={t(msg`Person`)}>
          <Select
            options={relTargets()}
            value={relTarget()}
            onChange={setRelTarget}
            aria-label={t(msg`Person`)}
          />
        </Field>
        <Field id="fr-kind" label={t(msg`Relation`)}>
          <Select
            options={RELATION_KINDS.map((k) => ({ value: k, label: relationLabel(k) }))}
            value={relKind()}
            onChange={setRelKind}
            aria-label={t(msg`Relation`)}
          />
        </Field>
      </FormDialog>

      <FormDialog
        open={memOpen()}
        onOpenChange={setMemOpen}
        title={memId() ? t(msg`Edit membership`) : t(msg`Add membership`)}
        submitTestId="membership-submit"
        disabled={!memChurch()}
        onSubmit={saveMembership}
      >
        <Show when={!memId()}>
          <Field id="mem-church" label={t(msg`Church`)}>
            <Select
              options={writableChurches().map((c) => ({ value: c.id, label: c.name ?? c.id }))}
              value={memChurch()}
              onChange={setMemChurch}
              aria-label={t(msg`Church`)}
            />
          </Field>
        </Show>
        <Field id="mem-status" label={t(msg`Status`)}>
          <Select
            options={MEMBERSHIP_STATUS.map((s) => ({ value: s, label: statusLabel(s) }))}
            value={memStatus()}
            onChange={setMemStatus}
            aria-label={t(msg`Status`)}
          />
        </Field>
        <div class="flex items-center justify-between rounded-md border border-border px-3 py-2">
          <span class="text-sm">{t(msg`Baptized`)}</span>
          <Switch
            checked={memBaptized()}
            onCheckedChange={setMemBaptized}
            aria-label={t(msg`Baptized`)}
            data-testid="membership-baptized"
          />
        </div>
        <Show when={memBaptized()}>
          <Field
            id="mem-baptized-at"
            label={t(msg`Baptism date`)}
            type="date"
            value={memBaptizedAt()}
            onInput={setMemBaptizedAt}
            testId="membership-baptized-date"
          />
        </Show>
        <div class="flex items-center justify-between rounded-md border border-border px-3 py-2">
          <span class="text-sm">{t(msg`Serves in a group`)}</span>
          <Switch
            checked={memServes()}
            onCheckedChange={setMemServes}
            aria-label={t(msg`Serves in a group`)}
          />
        </div>
        <Show when={memServes()}>
          <Field id="mem-serve-group" label={t(msg`Serving group`)}>
            <Select
              options={(groups.data ?? [])
                .filter((g) => g.church_id === memChurch())
                .map((g) => ({ value: g.id, label: g.name ?? g.id }))}
              value={memServeGroup()}
              onChange={setMemServeGroup}
              aria-label={t(msg`Serving group`)}
              emptyLabel={t(msg`No groups in this church yet`)}
            />
          </Field>
        </Show>
        <div class="grid grid-cols-2 gap-3">
          <div class="flex items-center justify-between rounded-md border border-border px-3 py-2">
            <span class="text-sm">{t(msg`Leader`)}</span>
            <Switch
              checked={memLeader()}
              onCheckedChange={setMemLeader}
              aria-label={t(msg`Leader`)}
            />
          </div>
          <div class="flex items-center justify-between rounded-md border border-border px-3 py-2">
            <span class="text-sm">{t(msg`Pastor`)}</span>
            <Switch
              checked={memPastor()}
              onCheckedChange={setMemPastor}
              aria-label={t(msg`Pastor`)}
            />
          </div>
        </div>
      </FormDialog>
    </div>
  );
}
